import { memo, useEffect, useMemo, useRef, useState } from 'react';
import ChatBubble from '@/components/prompt-builder/ChatBubble';
import TypingIndicator from '@/components/prompt-builder/TypingIndicator';
import { Button } from '@/components/ui/button';
import { ArrowRight, Send } from 'lucide-react';
import { ClarifyingQuestionCard } from './messages/ClarifyingQuestionCard';
import { PromptArtifactCard } from './messages/PromptArtifactCard';
import { PastedOutputBlock } from './messages/PastedOutputBlock';
import { AnalysisArtifactCard } from './messages/AnalysisArtifactCard';
import { PasteOutputSlot } from './messages/PasteOutputSlot';
import { PlanApprovalCard } from './messages/PlanApprovalCard';
import { DraftErrorCard } from './messages/DraftErrorCard';
import { WorkflowToolSelector } from './messages/WorkflowToolSelector';
import { Loader2 as StreamLoader2 } from 'lucide-react';
import type { ToolRecommendation } from '@/components/prompt-builder/ToolRecommendationCards';
import type { StepState, StreamMessage } from '@/lib/workflowStreamProtocol';

interface WorkflowStreamProps {
  messages: StreamMessage[];
  /** When 'executing'/'finished', the stream is filtered to the active step. */
  workflowPhase?: 'discovery' | 'executing' | 'finished';
  isTyping: boolean;
  composeValue: string;
  onComposeChange: (next: string) => void;
  onSubmit: () => void;
  onClarifyAnswer?: (messageId: string, answer: string) => void;
  /** Inline-edit save handler: commits a new prompt version for the step. */
  onSavePromptEdit?: (stepId: string, newBody: string) => void;
  /** Remix handler: user supplied additional context to fold into a new draft. */
  onRemixPrompt?: (stepId: string, additionalContext: string) => void;
  /** Step IDs currently waiting on a remix call. */
  remixingStepIds?: Set<string>;
  /** Record a user's pick for one of the analysis decisions. */
  onRecordDecision?: (stepId: string, decision: { question: string; chosen: string }) => void;
  /** Append a prior-step output into the active step's prompt body. */
  onInlineCarryForward?: (stepId: string, newBody: string) => void;
  /** Map: stepId → list of carry-forward sources available from prior steps. */
  carryForwardByStep?: Map<string, import('@/lib/carryForward').CarryForwardSource[]>;
  workflowName?: string | null;
  onOpenInPromptBuilder?: (stepId: string) => void;
  /** When provided, the active step drives an inline paste-output / proceed UI below the messages. */
  activeStep?: StepState | null;
  nextStepLabel?: string;
  /** Used by PasteOutputSlot to route binary uploads through Supabase storage. */
  workflowId?: string;
  onAnalyzeOutput?: (stepId: string, text: string, meta?: { filename?: string; mimeType?: string }) => void;
  onProceedFromStep?: (stepId: string) => void;
  onIterateStep?: (stepId: string) => void;
  /** When true, render an inline "Start building" approval card. */
  awaitingPlanApproval?: boolean;
  planStepCount?: number;
  planName?: string | null;
  onStartBuilding?: () => void;
  /** Retry handler for the active step's draftError. */
  onRetryDraft?: (stepId: string) => void;
  /** Workflow-level tool recommendations (shown after proposal). */
  toolRecommendations?: ToolRecommendation[] | null;
  isLoadingToolRecs?: boolean;
  isOptimizingForTool?: boolean;
  selectedTool?: string | null;
  onSelectWorkflowTool?: (rec: ToolRecommendation) => void;
  composePlaceholder?: string;
  disabled?: boolean;
  /** Called when the user clicks the "Go to Prompt Builder" CTA after a task_redirect. */
  onGoToPromptBuilder?: () => void;
  /** Called when the user clicks "Start this step" on a proposed step they navigated to. */
  onTriggerDraft?: (stepId: string) => void;
  /** Whether all steps prior to the active step are done. */
  priorStepsDone?: boolean;
}

export function WorkflowStream({
  messages,
  workflowPhase = 'discovery',
  isTyping,
  composeValue,
  onComposeChange,
  onSubmit,
  onClarifyAnswer,
  onSavePromptEdit,
  onRemixPrompt,
  remixingStepIds,
  onRecordDecision,
  onInlineCarryForward,
  carryForwardByStep,
  workflowName,
  onOpenInPromptBuilder,
  activeStep,
  nextStepLabel,
  workflowId,
  onAnalyzeOutput,
  onProceedFromStep,
  onIterateStep,
  awaitingPlanApproval,
  planStepCount,
  planName,
  onStartBuilding,
  onRetryDraft,
  toolRecommendations,
  isLoadingToolRecs,
  isOptimizingForTool,
  selectedTool,
  onSelectWorkflowTool,
  composePlaceholder,
  disabled,
  onGoToPromptBuilder,
  onTriggerDraft,
  priorStepsDone,
}: WorkflowStreamProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Track which step's prompt artifact is currently in inline-edit mode so we
  // can hide the paste-output slot (it would compete for attention while editing).
  const [editingStepIds, setEditingStepIds] = useState<Set<string>>(new Set());

  // Map of stepId → message id of its latest prompt_artifact (the only one that
  // should be inline-editable; older versions are read-only history).
  const latestArtifactIdByStep = useMemo(() => {
    const m = new Map<string, string>();
    for (const msg of messages) {
      if (msg.kind === 'prompt_artifact') m.set(msg.stepId, msg.id);
    }
    return m;
  }, [messages]);

  // Per-step view: during discovery we show all workflow-level messages;
  // during executing/finished we show only messages tied to the active step.
  // Clicking a different step on the canvas re-targets this filter.
  const visibleMessages = useMemo(() => {
    if (workflowPhase === 'discovery') {
      return messages.filter(m => !('stepId' in m) || !m.stepId);
    }
    if (!activeStep) return [];
    return messages.filter(m => 'stepId' in m && m.stepId === activeStep.id);
  }, [messages, workflowPhase, activeStep]);

  // Scroll to the bottom of the message list. Uses scrollTop on the container
  // instead of scrollIntoView — the latter scrolls ALL ancestors (even those
  // with overflow:hidden) and was pushing the entire layout off-screen.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [visibleMessages.length, activeStep?.id, isTyping]);

  const handleEditingChange = (stepId: string, editing: boolean) => {
    setEditingStepIds(prev => {
      const next = new Set(prev);
      if (editing) next.add(stepId);
      else next.delete(stepId);
      return next;
    });
  };

  const activeStepIsEditing = activeStep ? editingStepIds.has(activeStep.id) : false;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !disabled) {
      e.preventDefault();
      onSubmit();
    }
  };

  // Auto-grow the textarea as the user types. Caps at 200px tall.
  const handleComposeInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onComposeChange(e.target.value);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  };

  // Also reset height when the value is cleared externally (e.g. after submit).
  useEffect(() => {
    if (inputRef.current && composeValue === '') {
      inputRef.current.style.height = 'auto';
    }
  }, [composeValue]);

  return (
    <section className="flex flex-1 flex-col min-h-0 min-w-0 border-r border-border">
      {/* Per-step breadcrumb so the user knows which step's history they're seeing. */}
      {workflowPhase !== 'discovery' && activeStep && (
        <div className="flex items-center gap-2 px-4 md:px-6 py-2 text-[11px] text-muted-foreground border-b border-border/60 shrink-0">
          <span className="font-mono tabular-nums">step {activeStep.position + 1}</span>
          <span>·</span>
          <span className="font-medium text-foreground truncate">{activeStep.stepName}</span>
          <span className="ml-auto opacity-60">click another step on the canvas to switch views</span>
        </div>
      )}

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-5 space-y-4 min-h-0" style={{ overscrollBehavior: 'contain' }}>
        {workflowPhase !== 'discovery' && activeStep && visibleMessages.length === 0 && !isTyping && (
          <div className="text-center text-xs text-muted-foreground py-8">
            Nothing here yet for <span className="font-medium text-foreground">{activeStep.stepName}</span>. Cosmo will start when you reach this step.
          </div>
        )}
        {visibleMessages.map((m) => (
          <MessageRenderer
            key={m.id}
            message={m}
            isLatestArtifactForStep={
              m.kind === 'prompt_artifact' && latestArtifactIdByStep.get(m.stepId) === m.id
            }
            isRemixing={
              m.kind === 'prompt_artifact' && !!remixingStepIds?.has(m.stepId)
            }
            onClarifyAnswer={onClarifyAnswer ? (a) => onClarifyAnswer(m.id, a) : undefined}
            onSavePromptEdit={onSavePromptEdit}
            onRemixPrompt={onRemixPrompt}
            onInlineCarryForward={onInlineCarryForward}
            carryForwardSources={m.kind === 'prompt_artifact' ? carryForwardByStep?.get(m.stepId) : undefined}
            workflowName={workflowName}
            onEditingChange={handleEditingChange}
            onOpenInPromptBuilder={onOpenInPromptBuilder}
            onGoToPromptBuilder={onGoToPromptBuilder}
          />
        ))}
        {/* Proposed-step CTA: user navigated to a step that hasn't been started yet. */}
        {workflowPhase === 'executing' && activeStep && activeStep.phase === 'proposed' && !activeStep.promptDraft && onTriggerDraft && (
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex flex-col gap-2 items-start">
            <p className="text-sm font-medium text-foreground">{activeStep.stepName}</p>
            {priorStepsDone ? (
              <>
                <p className="text-xs text-muted-foreground">This step is ready to start. Cosmo will draft a prompt for {activeStep.recommendedTool ?? 'the recommended tool'}.</p>
                <Button size="sm" onClick={() => onTriggerDraft(activeStep.id)} className="mt-1 gap-1.5">
                  Start this step
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Complete the earlier steps first, then come back here to start.</p>
            )}
          </div>
        )}

        {/* Workflow-level tool selector (shown after proposal, before plan approval) */}
        {workflowPhase === 'discovery' && toolRecommendations && onSelectWorkflowTool && (
          <WorkflowToolSelector
            recommendations={toolRecommendations}
            selectedTool={selectedTool ?? null}
            isOptimizing={isOptimizingForTool ?? false}
            onSelectTool={onSelectWorkflowTool}
          />
        )}
        {workflowPhase === 'discovery' && isLoadingToolRecs && !toolRecommendations && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <StreamLoader2 className="h-4 w-4 animate-spin" />
            Finding the best tool for this workflow...
          </div>
        )}

        {/* Plan-approval CTA (discovery complete, awaiting user OK to start).
            Gated on tool selection — user must pick a tool first. */}
        {awaitingPlanApproval && onStartBuilding && selectedTool && (
          <PlanApprovalCard
            stepCount={planStepCount ?? 0}
            workflowName={planName}
            onStartBuilding={onStartBuilding}
          />
        )}

        {/* Drafting error for the active step (timeout, network, server error). */}
        {activeStep && activeStep.draftError && (
          <DraftErrorCard
            error={activeStep.draftError}
            stepName={activeStep.stepName}
            onRetry={onRetryDraft ? () => onRetryDraft(activeStep.id) : undefined}
            onOpenInPromptBuilder={onOpenInPromptBuilder ? () => onOpenInPromptBuilder(activeStep.id) : undefined}
          />
        )}

        {/* Inline pending action for the active step.
            Hidden while the user is inline-editing the prompt artifact OR has an error. */}
        {activeStep && activeStep.phase === 'prompt_ready' && onAnalyzeOutput && !activeStepIsEditing && !activeStep.draftError && (
          <PasteOutputSlot
            toolName={activeStep.recommendedTool ?? activeStep.promptDraft?.tool}
            workflowId={workflowId}
            onAnalyze={(text, meta) => onAnalyzeOutput(activeStep.id, text, meta)}
          />
        )}
        {activeStep && activeStep.phase === 'analyzing' && (
          <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4 text-xs text-muted-foreground text-center">
            Analyzing output…
          </div>
        )}
        {activeStep && activeStep.phase === 'done' && activeStep.analysis && (onProceedFromStep || onIterateStep) && (
          <AnalysisArtifactCard
            analysis={activeStep.analysis}
            nextStepLabel={nextStepLabel}
            recordedDecisions={activeStep.decisions}
            onRecordDecision={onRecordDecision ? (d) => onRecordDecision(activeStep.id, d) : undefined}
            onProceed={onProceedFromStep ? () => onProceedFromStep(activeStep.id) : undefined}
            onIterate={onIterateStep ? () => onIterateStep(activeStep.id) : undefined}
          />
        )}

        {isTyping && <TypingIndicator />}
      </div>

      <div className="border-t border-border p-3 shrink-0">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={composeValue}
            onChange={handleComposeInput}
            onKeyDown={handleKeyDown}
            placeholder={composePlaceholder ?? 'Type your response…'}
            aria-label="Compose message"
            rows={1}
            disabled={disabled}
            className="flex-1 resize-none rounded-xl border border-border bg-muted/30 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50 overflow-y-auto"
            style={{ minHeight: '40px', maxHeight: '200px' }}
          />
          <Button onClick={onSubmit} disabled={disabled || !composeValue.trim()} size="icon" className="h-10 w-10 rounded-xl">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}

const MessageRenderer = memo(function MessageRenderer({
  message,
  isLatestArtifactForStep,
  isRemixing,
  onClarifyAnswer,
  onSavePromptEdit,
  onRemixPrompt,
  onInlineCarryForward,
  carryForwardSources,
  workflowName,
  onEditingChange,
  onOpenInPromptBuilder,
  onGoToPromptBuilder,
}: {
  message: StreamMessage;
  isLatestArtifactForStep?: boolean;
  isRemixing?: boolean;
  onClarifyAnswer?: (answer: string) => void;
  onSavePromptEdit?: (stepId: string, newBody: string) => void;
  onRemixPrompt?: (stepId: string, additionalContext: string) => void;
  onInlineCarryForward?: (stepId: string, newBody: string) => void;
  carryForwardSources?: import('@/lib/carryForward').CarryForwardSource[];
  workflowName?: string | null;
  onEditingChange?: (stepId: string, editing: boolean) => void;
  onOpenInPromptBuilder?: (stepId: string) => void;
  onGoToPromptBuilder?: () => void;
}) {
  switch (message.kind) {
    case 'chat':
      return <ChatBubble role={message.role} content={message.content} />;
    case 'system':
      return (
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground py-1">
          <div className="flex-1 h-px bg-border" />
          <span>{message.text}</span>
          <div className="flex-1 h-px bg-border" />
        </div>
      );
    case 'clarify':
      return (
        <ClarifyingQuestionCard
          clarify={message.payload}
          onAnswer={(a) => onClarifyAnswer?.(a)}
        />
      );
    case 'proposal':
      return (
        <ChatBubble
          role="assistant"
          content={
            `**${message.payload.name}** — proposed a ${message.payload.steps.length}-step ${message.payload.cadence.replace('_', ' ')} workflow on the canvas.\n\n` +
            message.payload.steps.map((s, i) => `${i + 1}. ${s.stepName}`).join('\n')
          }
        />
      );
    case 'prompt_artifact':
      // onOpenInPromptBuilder is intentionally unused — the Remix flow replaces
      // the old "Prompt Builder" escape. Kept on the props in case other call
      // sites still need it.
      void onOpenInPromptBuilder;
      return (
        <PromptArtifactCard
          draft={message.payload}
          editable={isLatestArtifactForStep}
          onSaveEdit={onSavePromptEdit ? (newBody) => onSavePromptEdit(message.stepId, newBody) : undefined}
          onEditingChange={onEditingChange ? (editing) => onEditingChange(message.stepId, editing) : undefined}
          onRemix={onRemixPrompt ? (ctx) => onRemixPrompt(message.stepId, ctx) : undefined}
          remixing={isRemixing}
          carryForwardSources={carryForwardSources}
          workflowName={workflowName}
          onInlineCarryForward={onInlineCarryForward ? (newBody) => onInlineCarryForward(message.stepId, newBody) : undefined}
          collapsedByDefault={!isLatestArtifactForStep}
        />
      );
    case 'pasted_output':
      return <PastedOutputBlock output={message.payload} />;
    case 'task_redirect':
      return (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex flex-col gap-3 items-start">
          <p className="text-sm text-muted-foreground">This looks like a single task — the Prompt Builder is a better fit.</p>
          <Button size="sm" onClick={onGoToPromptBuilder} className="gap-1.5">
            Open in Prompt Builder
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      );
    case 'analysis':
      // Analysis is rendered as the active-step inline action below the
      // messages list (with Proceed/Iterate/decision interactivity). Skipping
      // the in-stream copy so the two don't double-render.
      return null;
    default:
      return null;
  }
});
